import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PlusIcon,
  TrashIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

import type { QuestionarioContent, QuestionConfig, DimensaoConfig } from '../../services/contentSnapshotService';
import type { ValidationError } from '../../services/contentAdminService';

interface QuestionEditorProps {
  versionId: string;
  content: any;
  onChange: (content: any) => void;
  validationErrors: ValidationError[];
}

interface QuestionFormData {
  id: number;
  dimensao: string;
  texto: string;
  opcoes: Array<{ valor: number; texto: string }>;
}

export const QuestionEditor: React.FC<QuestionEditorProps> = ({
  versionId,
  content,
  onChange,
  validationErrors,
}) => {
  const [editingQuestion, setEditingQuestion] = useState<number | null>(null);
  const [questionForm, setQuestionForm] = useState<QuestionFormData>({
    id: 0,
    dimensao: '',
    texto: '',
    opcoes: [
      { valor: 1, texto: 'Nunca' },
      { valor: 2, texto: 'Raramente' },
      { valor: 3, texto: 'Às vezes' },
      { valor: 4, texto: 'Frequentemente' },
      { valor: 5, texto: 'Sempre' },
    ],
  });

  const questions: QuestionarioContent = content.questions || {
    titulo: 'Avaliação de Perfil de Comunicação',
    descricao: 'Questionário para identificar perfil de comunicação',
    tempo_estimado: '8-12 minutos',
    total_questoes: 0,
    instrucoes: 'Responda as questões de acordo com sua experiência',
    escala: {
      tipo: 'likert_5',
      opcoes: [
        { valor: 1, texto: 'Nunca' },
        { valor: 2, texto: 'Raramente' },
        { valor: 3, texto: 'Às vezes' },
        { valor: 4, texto: 'Frequentemente' },
        { valor: 5, texto: 'Sempre' },
      ],
    },
    dimensoes: [],
    questoes: [],
  };

  const updateQuestions = (newQuestions: QuestionarioContent) => {
    onChange({
      ...content,
      questions: newQuestions,
    });
  };

  const handleAddQuestion = () => {
    const newId = Math.max(0, ...questions.questoes.map(q => q.id)) + 1;
    setQuestionForm({
      ...questionForm,
      id: newId,
    });
    setEditingQuestion(newId);
  };

  const handleSaveQuestion = () => {
    if (!questionForm.texto.trim() || !questionForm.dimensao) return;

    const existingIndex = questions.questoes.findIndex(q => q.id === questionForm.id);
    let newQuestions = [...questions.questoes];

    if (existingIndex >= 0) {
      newQuestions[existingIndex] = questionForm;
    } else {
      newQuestions.push(questionForm);
    }

    updateQuestions({
      ...questions,
      questoes: newQuestions.sort((a, b) => a.id - b.id),
      total_questoes: newQuestions.length,
    });

    setEditingQuestion(null);
    setQuestionForm({
      id: 0,
      dimensao: '',
      texto: '',
      opcoes: [...questionForm.opcoes],
    });
  };

  const handleEditQuestion = (question: QuestionConfig) => {
    setQuestionForm(question);
    setEditingQuestion(question.id);
  };

  const handleDeleteQuestion = (questionId: number) => {
    const newQuestions = questions.questoes.filter(q => q.id !== questionId);
    updateQuestions({
      ...questions,
      questoes: newQuestions,
      total_questoes: newQuestions.length,
    });
  };

  const handleMoveQuestion = (questionId: number, direction: 'up' | 'down') => {
    const currentIndex = questions.questoes.findIndex(q => q.id === questionId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= questions.questoes.length) return;

    const newQuestions = [...questions.questoes];
    [newQuestions[currentIndex], newQuestions[newIndex]] = [newQuestions[newIndex], newQuestions[currentIndex]];

    updateQuestions({
      ...questions,
      questoes: newQuestions,
    });
  };

  const handleUpdateBasicInfo = (field: string, value: string) => {
    updateQuestions({
      ...questions,
      [field]: value,
    });
  };

  const handleAddDimension = () => {
    const newDimension: DimensaoConfig = {
      id: `dim_${Date.now()}`,
      nome: 'Nova Dimensão',
      descricao: 'Descrição da dimensão',
    };

    updateQuestions({
      ...questions,
      dimensoes: [...questions.dimensoes, newDimension],
    });
  };

  const handleUpdateDimension = (index: number, field: string, value: string) => {
    const newDimensoes = [...questions.dimensoes];
    newDimensoes[index] = { ...newDimensoes[index], [field]: value };

    updateQuestions({
      ...questions,
      dimensoes: newDimensoes,
    });
  };

  const handleDeleteDimension = (index: number) => {
    const newDimensoes = questions.dimensoes.filter((_, i) => i !== index);
    updateQuestions({
      ...questions,
      dimensoes: newDimensoes,
    });
  };

  const getValidationErrors = (field: string) => {
    return validationErrors.filter(error => error.field.includes(field));
  };

  return (
    <div className="space-y-8">
      {/* Basic Information */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Informações Básicas
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Título
            </label>
            <input
              type="text"
              value={questions.titulo}
              onChange={(e) => handleUpdateBasicInfo('titulo', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tempo Estimado
            </label>
            <input
              type="text"
              value={questions.tempo_estimado}
              onChange={(e) => handleUpdateBasicInfo('tempo_estimado', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Descrição
            </label>
            <textarea
              value={questions.descricao}
              onChange={(e) => handleUpdateBasicInfo('descricao', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Instruções
            </label>
            <textarea
              value={questions.instrucoes}
              onChange={(e) => handleUpdateBasicInfo('instrucoes', e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Dimensions */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Dimensões Psicológicas
          </h3>
          <button
            onClick={handleAddDimension}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            Adicionar
          </button>
        </div>

        <div className="space-y-4">
          {questions.dimensoes.map((dimensao, index) => (
            <div key={dimensao.id} className="border border-gray-200 rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ID
                  </label>
                  <input
                    type="text"
                    value={dimensao.id}
                    onChange={(e) => handleUpdateDimension(index, 'id', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome
                  </label>
                  <input
                    type="text"
                    value={dimensao.nome}
                    onChange={(e) => handleUpdateDimension(index, 'nome', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>

                <div className="flex items-end">
                  <button
                    onClick={() => handleDeleteDimension(index)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição
                </label>
                <textarea
                  value={dimensao.descricao}
                  onChange={(e) => handleUpdateDimension(index, 'descricao', e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Questions */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Questões ({questions.questoes.length})
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Gerencie as questões do questionário
            </p>
          </div>
          
          <button
            onClick={handleAddQuestion}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            Nova Questão
          </button>
        </div>

        {/* Question Form */}
        <AnimatePresence>
          {editingQuestion !== null && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-6 border border-blue-200 rounded-lg p-4 bg-blue-50"
            >
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ID da Questão
                    </label>
                    <input
                      type="number"
                      value={questionForm.id}
                      onChange={(e) => setQuestionForm({ ...questionForm, id: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Dimensão
                    </label>
                    <select
                      value={questionForm.dimensao}
                      onChange={(e) => setQuestionForm({ ...questionForm, dimensao: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Selecione uma dimensão</option>
                      {questions.dimensoes.map((dim) => (
                        <option key={dim.id} value={dim.id}>
                          {dim.nome}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Texto da Questão
                  </label>
                  <textarea
                    value={questionForm.texto}
                    onChange={(e) => setQuestionForm({ ...questionForm, texto: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Digite o texto da questão..."
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => {
                      setEditingQuestion(null);
                      setQuestionForm({
                        id: 0,
                        dimensao: '',
                        texto: '',
                        opcoes: [...questionForm.opcoes],
                      });
                    }}
                    className="px-3 py-1.5 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <XMarkIcon className="w-4 h-4 inline mr-1" />
                    Cancelar
                  </button>
                  <button
                    onClick={handleSaveQuestion}
                    disabled={!questionForm.texto.trim() || !questionForm.dimensao}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <CheckIcon className="w-4 h-4 inline mr-1" />
                    Salvar
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Questions List */}
        <div className="space-y-3">
          {questions.questoes.map((question, index) => (
            <div
              key={question.id}
              className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                      {question.id}
                    </span>
                    <span className="text-sm text-gray-600">
                      {questions.dimensoes.find(d => d.id === question.dimensao)?.nome || question.dimensao}
                    </span>
                  </div>
                  <p className="text-gray-900">{question.texto}</p>
                </div>

                <div className="flex items-center gap-1 ml-4">
                  <button
                    onClick={() => handleMoveQuestion(question.id, 'up')}
                    disabled={index === 0}
                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ArrowUpIcon className="w-4 h-4" />
                  </button>
                  
                  <button
                    onClick={() => handleMoveQuestion(question.id, 'down')}
                    disabled={index === questions.questoes.length - 1}
                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ArrowDownIcon className="w-4 h-4" />
                  </button>

                  <button
                    onClick={() => handleEditQuestion(question)}
                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>

                  <button
                    onClick={() => handleDeleteQuestion(question.id)}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {questions.questoes.length === 0 && (
          <div className="text-center py-12">
            <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-4">
              <PlusIcon className="w-6 h-6 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Nenhuma questão criada
            </h3>
            <p className="text-gray-600 mb-4">
              Comece adicionando sua primeira questão ao questionário.
            </p>
            <button
              onClick={handleAddQuestion}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              Adicionar Primeira Questão
            </button>
          </div>
        )}
      </div>

      {/* Validation Errors */}
      {getValidationErrors('questions').length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-red-800 mb-2">
            Erros de Validação:
          </h4>
          <ul className="text-sm text-red-700 space-y-1">
            {getValidationErrors('questions').map((error, index) => (
              <li key={index}>• {error.message}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};